import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Loader2, BookOpen, Languages, Sparkles, BarChart3, Network } from 'lucide-react';
import axios from 'axios';
import { localizeReference } from '../utils/bookTranslations';
import { LS } from '../utils/lang';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StrongsModal = ({ strongsId, isOpen, onClose, translation = 'KJV' }) => {
  const [loading, setLoading] = useState(false);
  const [entry, setEntry] = useState(null);
  const [error, setError] = useState(null);
  const [showConcordance, setShowConcordance] = useState(false);
  const [concordanceVerses, setConcordanceVerses] = useState([]);
  const [loadingConcordance, setLoadingConcordance] = useState(false);
  const [study, setStudy] = useState(null);
  const [loadingStudy, setLoadingStudy] = useState(false);

  // Determine if Spanish translation
  const isSpanish = translation && (
    translation.includes('RV') || 
    translation.includes('NVI') || 
    translation.includes('LBLA') || 
    translation.includes('BSO') ||
    translation === 'DHH' ||
    translation === 'PDT' ||
    translation === 'TLA' ||
    translation === 'NTV'
  );
  
  const language = isSpanish ? 'spanish' : 'english';

  useEffect(() => {
    if (isOpen && strongsId) {
      loadStrongsEntry();
      setShowConcordance(false);
      setConcordanceVerses([]);
      setStudy(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, strongsId, language]);

  const loadWordStudy = async () => {
    if (study || loadingStudy) return;
    setLoadingStudy(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/strongs/${strongsId}/word-study`, {
        params: { lang: isSpanish ? 'es' : 'en' }
      });
      setStudy(res.data);
    } catch (err) {
      console.warn('Word study load failed:', err);
    } finally {
      setLoadingStudy(false);
    }
  };

  // Auto-load study when entry loads (cheap server-side aggregation).
  useEffect(() => {
    if (entry && !study && !loadingStudy) loadWordStudy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry]);

  const loadStrongsEntry = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/strongs/${strongsId}`, {
        params: { language }
      });
      setEntry(response.data);
    } catch (err) {
      setError('Failed to load Strong\'s definition');
      console.error('Error loading Strong\'s entry:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadConcordance = async () => {
    setLoadingConcordance(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/strongs/${strongsId}/verses`, {
        params: { lang: isSpanish ? 'es' : 'en' }
      });
      setConcordanceVerses(response.data.verses || []);
      setShowConcordance(true);
    } catch (err) {
      console.error('Error loading concordance:', err);
      setConcordanceVerses([]);
    } finally {
      setLoadingConcordance(false);
    }
  };

  const getLanguageLabel = (lang) => {
    if (isSpanish) {
      return lang === 'greek' ? 'Griego' : 'Hebreo';
    }
    return lang === 'greek' ? 'Greek' : 'Hebrew';
  };

  const getLanguageColor = (lang) => {
    return lang === 'greek' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' : 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
  };
  
  const labels = isSpanish ? {
    title: 'Concordancia de Strong',
    loading: 'Cargando definición...',
    failed: 'No se pudo cargar la definición de Strong',
    strongsDef: 'DEFINICIÓN DE STRONG',
    kjvTranslation: 'TRADUCCIÓN RVR',
    derivation: 'DERIVACIÓN',
    viewVerses: 'Ver Todos los Versículos'
  } : {
    title: "Strong's Concordance",
    loading: 'Loading definition...',
    failed: "Failed to load Strong's definition",
    strongsDef: "STRONG'S DEFINITION",
    kjvTranslation: 'KJV TRANSLATION',
    derivation: 'DERIVATION',
    viewVerses: 'View All Verses Using This Word'
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <BookOpen className="w-6 h-6 text-blue-600" />
            <span>{LS("Strong's Concordance", 'Concordancia de Strong')}</span>
            {strongsId && (
              <Badge variant="outline" className="text-lg font-mono">
                {strongsId}
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {isSpanish
              ? 'Definición y estudio de palabra de Strong: lema original, transliteración, definiciones, estadísticas de uso y palabras relacionadas.'
              : "Strong's word entry: original lemma, transliteration, definitions, usage statistics and related words."}
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            <span className="ml-3 text-slate-600">Loading definition...</span>
          </div>
        )}

        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        {entry && !loading && (
          <div className="space-y-6">
            {/* Original Word */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 p-6 rounded-lg border border-blue-100 dark:border-blue-800">
              <div className="flex items-center gap-3 mb-3">
                <Languages className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getLanguageColor(entry.language)}`}>
                  {getLanguageLabel(entry.language)}
                </span>
              </div>
              <div className="text-4xl font-bold text-slate-800 dark:text-slate-100 mb-2">
                {entry.lemma}
              </div>
              <div className="text-lg text-slate-600 dark:text-slate-300 italic">
                {entry.translit}
              </div>
              {entry.pronunciation && (
                <div className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                  {isSpanish ? 'Pronunciación' : 'Pronunciation'}: {entry.pronunciation}
                </div>
              )}
            </div>

            {/* Definition */}
            <div>
              <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                {labels.strongsDef}
              </h3>
              <p className="text-base text-slate-800 dark:text-slate-200 leading-relaxed">
                {(isSpanish && entry.strongs_def_es) || entry.strongs_def}
              </p>
            </div>

            {/* KJV Definition */}
            {(entry.kjv_def || entry.kjv_def_es) && (
              <div>
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                  {labels.kjvTranslation}
                </h3>
                <p className="text-base text-slate-700 dark:text-slate-200 leading-relaxed">
                  {(isSpanish && entry.kjv_def_es) || entry.kjv_def}
                </p>
              </div>
            )}

            {/* Derivation */}
            {(entry.derivation || entry.derivation_es) && (
              <div className="bg-slate-50 dark:bg-slate-800 p-4 rounded-lg border border-slate-200 dark:border-slate-700">
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-2">
                  {labels.derivation}
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                  {(isSpanish && entry.derivation_es) || entry.derivation}
                </p>
              </div>
            )}

            {/* Concordance Section */}
            {/* Word Study card — usage stats + related words */}
            {(study || loadingStudy) && (
              <div
                className="rounded-lg border border-purple-200 dark:border-purple-800 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 p-4 space-y-3"
                data-testid="strongs-word-study-card"
              >
                <div className="flex items-center gap-2 text-sm font-semibold text-purple-700 dark:text-purple-300">
                  <Sparkles className="w-4 h-4" />
                  {isSpanish ? 'Estudio de palabra' : 'Word Study'}
                </div>

                {loadingStudy && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    {isSpanish ? 'Calculando estadísticas…' : 'Computing usage stats…'}
                  </div>
                )}

                {study && (study.total_occurrences ?? 0) > 0 && (
                  <>
                    <div className="grid grid-cols-2 gap-2 text-center">
                      <div className="rounded bg-white/70 dark:bg-slate-800/40 p-2">
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                          <BarChart3 className="w-3 h-3 inline mr-0.5" />
                          {isSpanish ? 'Ocurrencias' : 'Occurrences'}
                        </div>
                        <div className="text-lg font-semibold text-purple-700 dark:text-purple-300">
                          {study.total_occurrences ?? 0}
                        </div>
                      </div>
                      <div className="rounded bg-white/70 dark:bg-slate-800/40 p-2">
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                          {isSpanish ? 'Libros' : 'Books'}
                        </div>
                        <div className="text-lg font-semibold text-purple-700 dark:text-purple-300">
                          {study.books?.length ?? 0}
                        </div>
                      </div>
                    </div>

                    {study.books?.length > 0 && (
                      <div>
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
                          {isSpanish ? 'Libros frecuentes' : 'Most frequent books'}
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {study.books.slice(0, 6).map((b, i) => (
                            <Badge
                              key={`${b.book}-${i}`}
                              variant="secondary"
                              className="text-[11px]"
                              data-testid={`strongs-top-book-${b.book}`}
                            >
                              {localizeReference(`${b.book} 1`, isSpanish).replace(/ 1$/, '')} · {b.count}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {study.related_words?.length > 0 && (
                      <div>
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1 flex items-center gap-1">
                          <Network className="w-3 h-3" />
                          {isSpanish ? 'Palabras relacionadas' : 'Related words'}
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {study.related_words.slice(0, 8).map((r) => (
                            <Badge
                              key={r.strongs_id}
                              variant="outline"
                              className="text-[11px] font-mono"
                              data-testid={`strongs-related-${r.strongs_id}`}
                            >
                              {r.strongs_id}
                              {r.overlap_count ? ` · ${r.overlap_count}` : ''}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Concordance Toggle */}
            <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
              <Button
                onClick={loadConcordance}
                disabled={loadingConcordance}
                variant="outline"
                className="w-full"
              >
                {loadingConcordance ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {isSpanish ? 'Cargando versículos...' : 'Loading verses...'}
                  </>
                ) : (
                  <>
                    <BookOpen className="w-4 h-4 mr-2" />
                    {labels.viewVerses}{showConcordance ? ` (${concordanceVerses.length})` : ''}
                  </>
                )}
              </Button>
            </div>

            {/* Concordance Results */}
            {showConcordance && concordanceVerses.length > 0 && (
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 max-h-64 overflow-y-auto">
                <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">
                  {isSpanish ? 'Concordancia - Todas las apariciones' : 'Concordance - All Occurrences'}
                </h3>
                <div className="space-y-2">
                  {concordanceVerses.map((verse, idx) => (
                    <div key={idx} className="bg-white p-2 rounded border border-slate-200">
                      <div className="font-semibold text-sm text-blue-700">{localizeReference(verse.reference, isSpanish)}</div>
                      <div className="text-xs text-slate-600 mt-1">{verse.context}</div>
                      <div className="text-xs text-slate-500 mt-1">{isSpanish ? 'Palabra' : 'Word'}: "{verse.word}"</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {showConcordance && concordanceVerses.length === 0 && (
              <div className="text-center text-slate-500 py-4">
                {isSpanish ? 'No se encontraron versículos.' : 'No verses found in current database.'}
              </div>
            )}

            {/* Info Footer */}
            <div className="pt-4 border-t border-slate-200">
              <p className="text-xs text-slate-500">
                {isSpanish
                  ? 'La Concordancia Strong es un índice de cada palabra de la Biblia que la vincula con el texto original en hebreo o griego.'
                  : "Strong's Concordance is a comprehensive index of every word in the King James Version of the Bible, linking each word back to the original Hebrew or Greek text."}
              </p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default StrongsModal;
